import { Plus, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Input, Select } from "../../components/ui/Input";
import { useCatalogFacets } from "../products/useCatalogFacets";
import { useCatalogLabels } from "../products/useCatalogLabels";
import type { MatchTrigger, SelectorKind } from "../../lib/types";
import { Hint } from "../../components/ui/Tooltip";
import { ProductPicker } from "./ProductPicker";

const KIND_LABELS: Record<SelectorKind, string> = {
	product: "Specific products",
	variant: "Specific variants",
	product_type: "Product type",
	vendor: "Vendor",
	tag: "Tag",
	any: "Anything in the cart",
};

const OFFER_KINDS: SelectorKind[] = ["product", "variant", "product_type", "vendor", "any"];

const MATCH_VALUE = "match:product_type";

const fallbackLabel = (value: string): string => {
	if (!value.startsWith("gid://")) return value;
	return `Product ${value.split("/").pop() ?? ""}`.trim();
};

export type SelectorFieldProps = {
	label: string;
	kind: SelectorKind;
	values: string[];
	matchTrigger?: MatchTrigger | null;
	allowMatchTrigger?: boolean;
	kinds?: SelectorKind[];
	/** Passed to the product picker, for offers that need a selling plan. */
	subscribableOnly?: boolean;
	/** Shown as a hint beside the label. */
	help?: ReactNode;
	onChange: (next: {
		kind: SelectorKind;
		values: string[];
		matchTrigger: MatchTrigger | null;
	}) => void;
};

/**
 * One control for both sides of a rule. The value editor follows the kind: a
 * product picker for gids, a suggestion list for the free text kinds, and
 * nothing at all when the rule matches whatever triggered it.
 */
export const SelectorField = ({
	label,
	kind,
	values,
	matchTrigger = null,
	allowMatchTrigger = false,
	kinds = OFFER_KINDS,
	subscribableOnly = false,
	help,
	onChange,
}: SelectorFieldProps) => {
	const facets = useCatalogFacets();
	const labels = useCatalogLabels(values);
	const [picking, setPicking] = useState(false);
	const [draft, setDraft] = useState("");

	const usesPicker = kind === "product" || kind === "variant";
	const usesText = kind === "product_type" || kind === "vendor" || kind === "tag";

	const suggestions =
		kind === "product_type"
			? (facets.data?.productTypes ?? [])
			: kind === "vendor"
				? (facets.data?.vendors ?? [])
				: kind === "tag"
					? (facets.data?.tags ?? [])
					: [];

	const emit = (
		next: Partial<{ kind: SelectorKind; values: string[]; matchTrigger: MatchTrigger | null }>,
	) => onChange({ kind, values, matchTrigger, ...next });

	const add = (value: string) => {
		const trimmed = value.trim();
		if (!trimmed || values.includes(trimmed)) return;
		emit({ values: [...values, trimmed] });
		setDraft("");
	};

	return (
		<div className="space-y-2">
			{help ? (
				<span className="flex items-center gap-1.5">
					<span className="text-xs font-medium text-ink-faint uppercase">{label}</span>
					<Hint label={`About ${label.toLowerCase()}`}>{help}</Hint>
				</span>
			) : (
				<span className="text-xs font-medium text-ink-faint uppercase">{label}</span>
			)}

			<Select
				value={matchTrigger ? MATCH_VALUE : kind}
				aria-label={`${label} kind`}
				onChange={(event) => {
					if (event.target.value === MATCH_VALUE) {
						emit({ matchTrigger: "product_type", values: [] });
						return;
					}
					emit({ kind: event.target.value as SelectorKind, values: [], matchTrigger: null });
				}}
			>
				{kinds.map((entry) => (
					<option key={entry} value={entry}>
						{KIND_LABELS[entry]}
					</option>
				))}
				{allowMatchTrigger ? (
					<option value={MATCH_VALUE}>Same product type as purchased</option>
				) : null}
			</Select>

			{matchTrigger ? (
				<p className="text-xs text-ink-muted">
					The reward has to be the same product type as the item that triggered the offer.
				</p>
			) : null}

			{!matchTrigger && kind === "any" ? (
				<p className="text-xs text-ink-muted">Every line in the cart qualifies.</p>
			) : null}

			{!matchTrigger && (usesPicker || usesText) ? (
				<>
					{values.length > 0 ? (
						<div className="flex flex-wrap gap-1.5">
							{values.map((value) => (
								<Badge key={value} variant="outline" className="gap-1.5">
									{labels.data?.labels[value] ?? fallbackLabel(value)}
									<button
										type="button"
										aria-label={`Remove ${value}`}
										className="text-ink-faint hover:text-ink"
										onClick={() => emit({ values: values.filter((entry) => entry !== value) })}
									>
										<X className="h-3 w-3" strokeWidth={2} />
									</button>
								</Badge>
							))}
						</div>
					) : null}

					{usesPicker ? (
						<Button variant="outline" size="sm" onClick={() => setPicking(true)}>
							<Plus className="h-3.5 w-3.5" strokeWidth={2} />
							Add product
						</Button>
					) : (
						<div className="flex gap-2">
							<Input
								value={draft}
								list={`${label}-suggestions`}
								placeholder={`Add a ${KIND_LABELS[kind].toLowerCase()}`}
								className="max-w-xs"
								onChange={(event) => setDraft(event.target.value)}
								onKeyDown={(event) => {
									if (event.key !== "Enter") return;
									event.preventDefault();
									add(draft);
								}}
							/>
							<datalist id={`${label}-suggestions`}>
								{suggestions.map((entry) => (
									<option key={entry} value={entry} />
								))}
							</datalist>
							<Button variant="outline" size="sm" onClick={() => add(draft)}>
								Add
							</Button>
						</div>
					)}
				</>
			) : null}

			<ProductPicker
				subscribableOnly={subscribableOnly}
				open={picking}
				onOpenChange={(next) => !next && setPicking(false)}
				title={`Add to ${label.toLowerCase()}`}
				onPick={(picked) => {
					const gid = kind === "variant" ? picked.variantGid : picked.productGid;
					if (gid) add(gid);
					setPicking(false);
				}}
			/>
		</div>
	);
};
